import React, { useRef, useState, useEffect } from 'react';
import moment from 'moment';
import { EChartOption } from 'echarts';
import { get } from 'lodash';
import * as echarts from 'echarts/lib/echarts';
import { UNITS, ALL_DISPLAY_NAMES } from '@components/session-timeline/utils/constants';
import errorImpact from '@components/session-timeline/utils/errorImpact';
import { humanize } from '@utils';
import formatBPS from '../utils/formatBPS';

const getInterval = (option: EChartOption) => {
  const singleAxis = get(option, ['singleAxis', 0], {}) as { min?: number; max?: number };
  const xAxis = get(option, ['xAxis', 0], {}) as { min?: number; max?: number };
  const start = singleAxis.min || xAxis.min || 0;
  const end = singleAxis.max || xAxis.max || 0;

  return [start, end];
};

const calcPixel = (start, end, width, current) => {
  const diff = end - start;
  return (width / diff) * (current - start);
};

export default (
  highlighMoment: number | null,
  event: string,
  dateRange: number[],
): [React.RefObject<HTMLDivElement>, number, string | React.ReactNode] => {
  const chartDOMRef = useRef<HTMLDivElement>(null);
  const [x, setX] = useState(0);
  const [text, setText] = useState<string | React.ReactNode>('');

  useEffect(() => {
    if (!chartDOMRef.current) {
      return;
    }
    const instance = echarts.getInstanceByDom(chartDOMRef.current);
    if (!instance || !highlighMoment) {
      return;
    }
    const min = dateRange[0];
    const max = dateRange[dateRange.length - 1];

    if (highlighMoment <= min || highlighMoment >= max) {
      setX(0);
      setText('');
      return;
    }

    const option = instance.getOption();
    const series = get(option, ['series', 0, 'data'], []) as any[][];
    const grid = get(option, ['grid', 0], {});
    const [start, end] = getInterval(option);
    const point = series.find((row) => row.some((column) => column === highlighMoment));
    const left = grid.left || 0;
    const right = grid.right || 0;
    let content: React.ReactNode = '';
    if (Array.isArray(point)) {
      const pixel =
        calcPixel(start, end, instance.getWidth() - left - right, highlighMoment) + left;
      setX(pixel);

      const errorDetails = point.find((row) => typeof row === 'object');
      if (errorDetails) {
        content = (
          <>
            <div>
              <strong>{moment(highlighMoment).format('MMM DD, YYYY HH:mm:ss')}</strong>
            </div>
            <div>{errorDetails.errorMessage}</div>
            <div>{ALL_DISPLAY_NAMES[errorDetails.type] || errorDetails.type}</div>
            <div>Impact: {errorImpact(errorDetails)}</div>
          </>
        );
      } else if (point.length === 2) {
        let value = humanize(point[1], '');
        let unit = UNITS[event];
        if (unit === 'bps') {
          [value, unit] = formatBPS(point[1], event);
        }
        content = (
          <>
            <div>
              <strong>{moment(point[0]).format('MMM DD, YYYY HH:mm:ss')}</strong>
            </div>
            <div>
              <span>{value}</span>
              <span>{unit}</span>
            </div>
          </>
        );
      }
    } else {
      setX(0);
    }
    setText(content);
  }, [chartDOMRef.current, highlighMoment, dateRange]);

  return [chartDOMRef, x, text];
};
